import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Calendar } from 'react-date-range';
import { Button } from "@material-tailwind/react";
import { HiOutlineCalendar } from 'react-icons/hi';
import './datePicker.scss';

const DatePicker = ({
    getDateRange = () => {},
    initialDateRange,
    initiallySelected = false,
    highlightedDatesSet = new Set()
}) => {

    const datePickerRef = useRef(null);

    // Calculate available months for each year
    const availableYearsAndMonths = useMemo(() => {
        const result = {}; // { year: Set<monthIndex> }
        let minYear = Infinity, maxYear = -Infinity;
        let firstAvailableDate = null;
        let lastAvailableDate = null;

        if (highlightedDatesSet.size > 0) {
            const sortedDates = Array.from(highlightedDatesSet).sort(); // Sort as strings for YYYY-MM-DD
            const parseDateString = (dateString) => {
                const [year, month, day] = dateString.split('-').map(Number);
                return new Date(year, month - 1, day);
            };

            firstAvailableDate = parseDateString(sortedDates[0]);
            lastAvailableDate = parseDateString(sortedDates[sortedDates.length - 1]);

            highlightedDatesSet.forEach((d) => {
                const [y, m] = d.split('-'); // format: yyyy-mm-dd
                const year = parseInt(y, 10);
                const month = parseInt(m, 10) - 1; // month index (0-11)
                if (!result[year]) result[year] = new Set();
                result[year].add(month);

                minYear = Math.min(minYear, year);
                maxYear = Math.max(maxYear, year);
            });
        }

        const minDateObj = firstAvailableDate;
        // Make sure maxDateObj doesn't go into the future beyond today
        const today = new Date();
        const maxDateObj = lastAvailableDate && lastAvailableDate < today ? lastAvailableDate : today;

        return {
            years: Object.keys(result).map(Number).sort((a, b) => a - b),
            monthsByYear: result,
            minAllowedDate: minDateObj,
            maxAllowedDate: maxDateObj,
            minYear: isFinite(minYear) ? minYear : null,
            maxYear: isFinite(maxYear) ? maxYear : null,
        };
    }, [highlightedDatesSet]);

    const {
        years: availableYears,
        monthsByYear,
        minAllowedDate,
        maxAllowedDate,
        minYear,
        maxYear
    } = availableYearsAndMonths;


    // Helper to find the first available month for a given year
    const findFirstAvailableMonthInYear = useCallback((year) => {
        if (monthsByYear[year]) {
            const sortedMonths = Array.from(monthsByYear[year]).sort((a, b) => a - b);
            if (sortedMonths.length > 0) {
                return sortedMonths[0]; // Return month index (0-11)
            }
        }
        return null;
    }, [monthsByYear]);

    // Initial state setup for date and monthView
    const [date, setDate] = useState(() => {
        if (minAllowedDate) return minAllowedDate;
        return new Date();
    });

    const [monthView, setMonthView] = useState(() => {
        if (minAllowedDate) return minAllowedDate;
        return new Date();
    });

    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectedRange, setSelectedRange] = useState(
        initiallySelected && initialDateRange ? initialDateRange : {}
    );


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
                setShowDatePicker(false);
            }
        };
        if (showDatePicker) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDatePicker]);

    const formatDateForLookup = d => {
        if (!d) return '';
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const customDayContent = day => {
        const formattedDay = formatDateForLookup(day);
        const isCurrentMonthView = day.getMonth() === monthView.getMonth() && day.getFullYear() === monthView.getFullYear();

        const isHighlighted = highlightedDatesSet.has(formattedDay);

        if (!isHighlighted) {
            const wrapperClasses = `day-content-wrapper ${!isCurrentMonthView ? 'day-content-wrapper--other-month' : ''}`;
            return (
                <div className={wrapperClasses}>
                    <span className="disabled-day">{day.getDate()}</span>
                </div>
            );
        }

        const isSelected = formatDateForLookup(day) === formatDateForLookup(date);

        const wrapperClasses = `day-content-wrapper available-day ${
            !isCurrentMonthView ? 'available-day--other-month' : ''
        }`.trim();

        const spanClasses = isSelected
            ? `selected-and-available-day ${!isCurrentMonthView ? 'selected-and-available-day--other-month' : ''}`
            : '';

        return (
            <div className={wrapperClasses}>
                <span className={spanClasses.trim()}>{day.getDate()}</span>
            </div>
        );
    };

    const convertDateToLocal = val => {
        if (!val || !(val instanceof Date)) return '';
        const day = String(val.getDate()).padStart(2, '0');
        const month = String(val.getMonth() + 1).padStart(2, '0');
        const year = val.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const handleSelect = newDate => {
        const formattedDay = formatDateForLookup(newDate);
        if (highlightedDatesSet.has(formattedDay)) {
            setDate(newDate);
            setMonthView(newDate); // Keep month view synchronized with selection
        }
    };

    const stableGetDateRange = useCallback(getDateRange, []);

    // Effect to set initial date and month view based on props or available data
    useEffect(() => {
        if (initialDateRange?.startDate) {
            const parts = initialDateRange.startDate.split('/');
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10);
                const year = parseInt(parts[2], 10);
                const initialDateObject = new Date(year, month - 1, day);

                // Ensure initialDate is valid and highlighted
                if ((!minAllowedDate || initialDateObject >= minAllowedDate) &&
                    (!maxAllowedDate || initialDateObject <= maxAllowedDate) &&
                    highlightedDatesSet.has(formatDateForLookup(initialDateObject))) {
                    setDate(initialDateObject);
                    setMonthView(initialDateObject);
                } else if (minAllowedDate) { // Fallback to minAllowedDate if initialDate is invalid
                    setDate(minAllowedDate);
                    setMonthView(minAllowedDate);
                }
            }
            setSelectedRange(initialDateRange);
            stableGetDateRange(initialDateRange);
        } else if (minAllowedDate && !initiallySelected) {
            // If no initialDateRange and minAllowedDate exists, set date and monthView to minAllowedDate
            setDate(minAllowedDate);
            setMonthView(minAllowedDate);
        }
    }, [initialDateRange, stableGetDateRange, minAllowedDate, maxAllowedDate, highlightedDatesSet, initiallySelected]);


    // Custom month change handler to skip to the next/previous available month
    const handleMonthChange = useCallback((newMonth) => {
        const newMonthYear = newMonth.getFullYear();
        const newMonthIndex = newMonth.getMonth();

        const currentAvailableMonths = monthsByYear[newMonthYear];

        if (currentAvailableMonths && currentAvailableMonths.has(newMonthIndex)) {
            // If the target month has data, just set it
            setMonthView(newMonth);
            // Optionally, set the date to the first available day in this month if needed
            // For now, keeping it simple, date will be handled by actual day selection
        } else {
            // Find the closest available month in the desired direction
            const direction = newMonth.getTime() > monthView.getTime() ? 1 : -1; // 1 for next, -1 for previous

            let targetYear = newMonthYear;
            let targetMonth = newMonthIndex;
            let found = false;

            // Iterate until an available month is found or we go out of bounds
            while (!found) {
                if (direction === 1) { // Moving forward
                    targetMonth++;
                    if (targetMonth > 11) {
                        targetMonth = 0;
                        targetYear++;
                    }
                } else { // Moving backward
                    targetMonth--;
                    if (targetMonth < 0) {
                        targetMonth = 11;
                        targetYear--;
                    }
                }

                // Check year boundaries
                if ((minYear !== null && targetYear < minYear) || (maxYear !== null && targetYear > maxYear)) {
                    // Reached an invalid year, stop searching
                    break;
                }

                if (monthsByYear[targetYear] && monthsByYear[targetYear].has(targetMonth)) {
                    // Found an available month
                    const newViewDate = new Date(targetYear, targetMonth, 1);
                     // Ensure newViewDate is not beyond maxAllowedDate
                    if (maxAllowedDate && newViewDate > maxAllowedDate) {
                        break; // Stop if we are trying to go past max allowed date
                    }
                    setMonthView(newViewDate);
                    // Find the first available day in this month
                    const firstAvailableDayInMonth = Array.from(highlightedDatesSet)
                        .filter(d => d.startsWith(`${targetYear}-${String(targetMonth + 1).padStart(2, '0')}`))
                        .sort()[0];

                    if (firstAvailableDayInMonth) {
                        const [y, m, d] = firstAvailableDayInMonth.split('-').map(Number);
                        setDate(new Date(y, m - 1, d));
                    } else {
                        setDate(newViewDate); // Fallback to first of month
                    }
                    found = true;
                }
            }
        }
    }, [monthView, monthsByYear, minYear, maxYear, maxAllowedDate, highlightedDatesSet]);


    const saveSelection = () => {
        const stringifiedDate = convertDateToLocal(date);
        const dateRangeObject = { startDate: stringifiedDate, endDate: stringifiedDate };
        setSelectedRange(dateRangeObject);
        getDateRange(dateRangeObject);
        setShowDatePicker(false);
    };

    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const customHeader = ({ date: headerDate, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => {
        const currentYear = headerDate.getFullYear();
        const currentMonthIndex = headerDate.getMonth();

        const handleYearChange = (event) => {
            const newYear = parseInt(event.target.value, 10);
            const firstAvailableMonth = findFirstAvailableMonthInYear(newYear);

            if (firstAvailableMonth !== null) {
                const newDateForView = new Date(newYear, firstAvailableMonth, 1);
                changeYear(newYear);
                changeMonth(firstAvailableMonth);
                setMonthView(newDateForView);
                // Also update the selected date to the first available day in this new month/year
                const firstAvailableDayInMonth = Array.from(highlightedDatesSet)
                    .filter(d => d.startsWith(`${newYear}-${String(firstAvailableMonth + 1).padStart(2, '0')}`))
                    .sort()[0];
                if (firstAvailableDayInMonth) {
                    const [y, m, d] = firstAvailableDayInMonth.split('-').map(Number);
                    setDate(new Date(y, m - 1, d));
                } else {
                    setDate(newDateForView); // Fallback if no specific day is highlighted
                }
            } else {
                // If no months are available for the selected year, you might want to
                // revert to the previous year or handle this case appropriately.
                // For now, it will just not change.
            }
        };

        const handleMonthSelect = (event) => {
            const newMonthIndex = parseInt(event.target.value, 10);
            const newDateForView = new Date(currentYear, newMonthIndex, 1);
            changeMonth(newMonthIndex);
            setMonthView(newDateForView);
            // Update selected date to the first available day in this new month
            const firstAvailableDayInMonth = Array.from(highlightedDatesSet)
                .filter(d => d.startsWith(`${currentYear}-${String(newMonthIndex + 1).padStart(2, '0')}`))
                .sort()[0];
            if (firstAvailableDayInMonth) {
                const [y, m, d] = firstAvailableDayInMonth.split('-').map(Number);
                setDate(new Date(y, m - 1, d));
            } else {
                setDate(newDateForView); // Fallback
            }
        };

        const currentYearMonths = monthsByYear[currentYear] ? Array.from(monthsByYear[currentYear]).sort((a,b) => a-b) : [];

        return (
            <div className="rdrCustomHeader">
                <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                    {'<'}
                </button>
                <select
                    value={currentYear}
                    onChange={handleYearChange}
                    className="rdrCustomYearPicker"
                >
                    {availableYears.map((year) => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>
                <select
                    value={currentMonthIndex}
                    onChange={handleMonthSelect}
                    className="rdrCustomMonthPicker"
                >
                    {currentYearMonths.map((monthIndex) => (
                        <option key={monthIndex} value={monthIndex}>
                            {monthNames[monthIndex]}
                        </option>
                    ))}
                </select>
                <button onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                    {'>'}
                </button>
            </div>
        );
    };

    return (
        <div ref={datePickerRef} className='date-picker-container font-Archivo'>
            <div>
                <Button
                    className="flex items-center justify-around gap-3 date-choose-btn w-full p-2.5 focus-ring"
                    variant='text'
                    onClick={() => setShowDatePicker(true)}
                >
                    { selectedRange?.startDate ? <>{selectedRange.startDate}</> : 'Choose Date' }
                    <HiOutlineCalendar />
                </Button>
            </div>
            {showDatePicker && (
                <div className='date-picker'>
                    <Calendar
                        date={date}
                        onChange={handleSelect}
                        color={'#EF805D'}
                        dayContentRenderer={customDayContent}
                        month={monthView} // Control the displayed month/year
                        onMonthChange={handleMonthChange} // Custom handler for arrow clicks
                        minDate={minAllowedDate || new Date(1900, 0, 1)}
                        maxDate={maxAllowedDate}
                        showPreview={false}
                        className='mt-1'
                        // Render the custom header
                        renderStaticRangeLabel={({ label }) => <span>{label}</span>} // Required prop
                        headerRenderer={customHeader}
                    />
                    <div className='date-picker-footer'>
                        <div className='selected-dates'>{convertDateToLocal(date)}</div>
                        <Button size='sm' variant='outlined' onClick={() => setShowDatePicker(false)}>Cancel</Button>
                        <Button size='sm' style={{backgroundColor: '#2573EB'}} className='apply-btn' onClick={saveSelection}>Apply</Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DatePicker;